import { useState, useEffect, useMemo } from "react";
import { apiGet, apiPost } from "../../../utils/apiClient";
import { formatDateTime } from "../../../utils/commonService";
import BuyerRequestFilter from "./BuyerRequestFilter";
import "../css/buyerRequest.css";

const BuyerRequestList = () => {
  const menuAuth = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      return user?.menuAuth || [];
    } catch {
      return [];
    }
  }, []);
  const hasPermission = (code) => menuAuth.includes(code);

  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(100000);
  const [sliderMax, setSliderMax] = useState(100000);
  const [priceRange, setPriceRange] = useState("100000");
  const [isManualInput, setIsManualInput] = useState(false);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 10;

  const loadRequests = async (page = 0) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiGet(`/store/nego-req/list?page=${page}&size=${pageSize}`);
      const data = await response.json();
      
      if (response.ok) {
        setCurrentPage(data.number);
        setTotalPages(data.totalPages);
        setTotalElements(data.totalElements);
        setRequests(data.content);
      } else {
        setError("구매자 요청 조회에 실패했습니다.");
      }
    } catch (err) {
      console.error("API 요청 실패:", err);
      setError("구매자 요청 조회 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const searchRequests = async (page = 0) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (searchText) params.append("productNm", searchText);
      if (startDate) params.append("startDateTime", startDate + "T00:00:00");
      if (endDate) params.append("endDateTime", endDate + "T23:59:59");
      if (minPrice || maxPrice) {
        params.append("minPriceRange", minPrice);
        params.append("maxPriceRange", maxPrice);
      }
      params.append("page", page);
      params.append("size", pageSize);

      const response = await apiGet(
        `/store/nego-req/search?${params.toString()}`
      );
      const data = await response.json();
      
      if (response.ok) {
        setCurrentPage(data.number);
        setTotalPages(data.totalPages);
        setTotalElements(data.totalElements);
        setRequests(data.content);
        console.log("구매자 요청 검색 결과:", data);
      } else {
        setError("구매자 요청 검색에 실패했습니다.");
      }
    } catch (err) {
      console.error("검색 API 요청 실패:", err);
      setError("구매자 요청 검색 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const handleReset = () => {
    setSearchText("");
    setStartDate("");
    setEndDate("");
    setStatusFilter("ALL");
    setMinPrice(0);
    setMaxPrice(100000);
    setSliderMax(100000);
    setPriceRange("100000");
    setIsManualInput(false);
    setCurrentPage(0);
    loadRequests(0);
  };

  const handleSearch = () => {
    setCurrentPage(0);
    searchRequests(0);
  };

  const handleResponse = async (negoRequestIdx, isAccept) => {
    const action = isAccept ? "승인" : "거절";
    const confirmed = window.confirm(`정말 이 요청을 ${action}하시겠습니까?`);
    
    if (!confirmed) return;

    try {
      const response = await apiPost(`/store/nego-req/${negoRequestIdx}`, { isAccept });
      
      if (response.ok) {
        alert(`요청이 ${action}되었습니다.`);
        
        // 목록 새로고침
        const hasSearchCondition = searchText || startDate || endDate || statusFilter !== "ALL" || minPrice > 0 || maxPrice < 100000;
        if (hasSearchCondition) {
          searchRequests(currentPage);
        } else {
          loadRequests(currentPage);
        }
      } else {
        alert(`요청 ${action}에 실패했습니다.`);
      }
    } catch (error) {
      console.error(`요청 ${action} API 호출 에러:`, error);
      alert(`요청 ${action} 중 오류가 발생했습니다.`);
    }
  };

  return (
    <div>
      <BuyerRequestFilter
        searchText={searchText}
        setSearchText={setSearchText}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        minPrice={minPrice}
        setMinPrice={setMinPrice}
        maxPrice={maxPrice}
        setMaxPrice={setMaxPrice}
        sliderMax={sliderMax}
        setSliderMax={setSliderMax}
        priceRange={priceRange}
        setPriceRange={setPriceRange}
        isManualInput={isManualInput}
        setIsManualInput={setIsManualInput}
        handleSearch={handleSearch}
        handleReset={handleReset}
        isHistory={false}
      />

      <div className="buyerRequest-table-wrap">
        <table className="buyerRequest-table">
          <thead>
            <tr>
              <th>번호</th>
              <th>상품명</th>
              <th>구매자명</th>
              <th>구매자 연락처</th>
              <th>요청가격</th>
              <th>요청일시</th>
              <th>상태</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" style={{textAlign: 'center', padding: '20px'}}>로딩 중...</td></tr>
            ) : error ? (
              <tr><td colSpan="7" style={{textAlign: 'center', padding: '20px', color: 'red'}}>{error}</td></tr>
            ) : requests.length === 0 ? (
              <tr><td colSpan="7" style={{textAlign: 'center', padding: '20px'}}>구매자 요청이 없습니다.</td></tr>
            ) : (
              requests.map((request, index) => (
                <tr key={request.productCode || index}>
                  <td>{index + 1}</td>
                  <td>
                    <div>
                        <span>{request.productNm}</span>
                    </div>
                  </td>
                  <td>{request.custNm}</td>
                  <td>{request.custTelNo}</td>
                  <td>{request.requestPrice?.toLocaleString()}원</td>
                  <td>{formatDateTime(request.requestDateTime)}</td>
                  <td>
                    {!request.requestStatus && (
                      <div className="buyerRequest-action-wrap">
                        <button
                          className="buyerRequest-btn"
                          disabled={!hasPermission("STORE_NEGO")}
                          onClick={() => handleResponse(request.negoRequestIdx, true)}
                        >
                          승인
                        </button>
                        <button
                          className="buyerRequest-btn outline danger"
                          disabled={!hasPermission("STORE_NEGO")}
                          onClick={() => handleResponse(request.negoRequestIdx, false)}
                        >
                          거절
                        </button>
                        {!hasPermission("STORE_NEGO") && (
                          <span className="buyerRequest-action-tooltip">해당 기능 사용 권한이 없습니다.</span>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="buyerRequest-footer">
          <div className="buyerRequest-count">
            전체 {totalElements}개 요청
          </div>
          <div className="buyerRequest-pagination">
            <button 
              className="buyerRequest-page" 
              onClick={() => {
                if (currentPage > 0) {
                  const newPage = currentPage - 1;
                  setCurrentPage(newPage);
                  loadRequests(newPage);
                }
              }}
              disabled={currentPage === 0}
            >
              이전
            </button>
            {[...Array(Math.max(1, totalPages))].map((_, index) => (
              <button
                key={index}
                className={`buyerRequest-page ${currentPage === index ? 'active' : ''}`}
                onClick={() => {
                  setCurrentPage(index);
                  loadRequests(index);
                }}
              >
                {index + 1}
              </button>
            ))}
            <button 
              className="buyerRequest-page"
              onClick={() => {
                if (currentPage < totalPages - 1) {
                  const newPage = currentPage + 1;
                  setCurrentPage(newPage);
                  loadRequests(newPage);
                }
              }}
              disabled={totalPages <= 1 || currentPage >= totalPages - 1}
            >
              다음
            </button>
          </div>
          <div className="buyerRequest-footer-spacer"></div>
        </div>
      </div>
    </div>
  );
};

export default BuyerRequestList;
